import { NavMain } from "./nav-main";
import { MessageCirclePlus, MessageCircleQuestion, Search } from "lucide-react";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarRail } from "./ui/sidebar";
import { NavSecondary } from "./nav-secondary";
import { NavChats } from "./nav-chats";
import { useInfiniteScrollThreads, useThreadsQuery } from "@/api/queries/ThreadQueries";
import ChatSearchDialog from "./dialogs/search/ChatSearchDialog";
import { useNavigationStore } from "@/stores/navigation-store";
import { useMemo } from "react";
import SidebarLogo from "./SidebarLogo";
import { cn } from "@/lib/utils";
import { useSettingsQuery } from "@/api/queries/admin/SettingsQueries";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export function ChatSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
    const { setSearchIsOpen, setHelpIsOpen } = useNavigationStore();
    const { data: settingsData } = useSettingsQuery(true);
    const branding = (settingsData as any)?.branding_settings;
    const sidebarLogoEnabled = branding?.sidebar_logo_enabled ?? true;
    const { t } = useTranslation();
    
    const data = {
        navMain: [
            {
                name: t("chat.newChat"),
                url: "/",
                icon: MessageCirclePlus,
            },
            {
                name: t("chat.search"),
                url: "#",
                icon: Search,
                onClick: () => {
                    setSearchIsOpen(true);
                },
            },
        ],
        navSecondary: [
            {
                title: t("chat.help"),
                url: "#",
                icon: MessageCircleQuestion,
                onClick: () => {
                    setHelpIsOpen(true);
                },
            },
        ],
    };
    const {
        items: chatItems,
        bottomRef: chatBottomRef,
        isFetchingNextPage: isFetchingNextPageChat,
        isLoading: isLoadingChat,
    } = useInfiniteScrollThreads({
        observer: { threshold: 0.8 },
        filter: {
            search: "",
        },
    });

    const {
        data: favouriteChatItems,
        isLoading: isLoadingFavouriteChat,
        isFetchingNextPage: isFetchingNextPageFavouriteChat,
        fetchNextPage: fetchNextPageFavouriteChat,
    } = useThreadsQuery({ search: "", isFavorite: true }, 50, true);

    const favouriteChats = useMemo(
        () =>
            favouriteChatItems?.pages
                .flatMap((page) => page.data)
                .map((chat) => ({ id: chat.id, name: chat.title!, url: `chats/${chat.id}` })) || [],
        [favouriteChatItems]
    );

    const normalChats = useMemo(
        () =>
            chatItems
                .filter((chat) =>
                    favouriteChatItems?.pages
                        .flatMap((page) => page.data)
                        .every((favouriteChat) => favouriteChat.id !== chat.id)
                )
                .map((chat) => ({ id: chat.id, name: chat.title!, url: `chats/${chat.id}` })) || [],
        [chatItems, favouriteChatItems]
    );

    return (
        <Sidebar className="border-r-0" {...props}>
            <SidebarHeader>
                {sidebarLogoEnabled && (
                    <Link to="/" className={cn(
                        "flex w-full flex-row justify-start gap-1 pl-2.5 py-2",
                        "items-center min-h-12"
                    )}>
                        <SidebarLogo className="w-full" alt="Chat logo" />
                    </Link>
                )}
                <NavMain items={data.navMain} disableHighlight />
                <ChatSearchDialog />
            </SidebarHeader>
            <SidebarContent>
                <NavChats
                    chats={favouriteChats}
                    isLoading={isLoadingFavouriteChat}
                    isFetchingNextPage={isFetchingNextPageFavouriteChat}
                    title={t("chat.favorites")}
                    fetchNextPage={fetchNextPageFavouriteChat}
                    hideShowMore={favouriteChats.length <= 3}
                />
                <NavChats
                    hideShowMore
                    chats={normalChats}
                    isLoading={isLoadingChat}
                    isFetchingNextPage={isFetchingNextPageChat}
                    bottomRef={chatBottomRef}
                    title={t("chat.chats")}
                />
            </SidebarContent>
            <SidebarFooter className="px-0">
                <NavSecondary items={data.navSecondary} />
            </SidebarFooter>
            <SidebarRail />
        </Sidebar>
    );
}
